import axios from "axios";
import { toast } from "react-toastify";
import { API_TIMEOUT, ERROR_MESSAGES, HTTP_STATUS, TIME_CONSTANTS } from "../constants/appConstants";

const normalizeBaseUrl = (url) => (url ? url.replace(/\/+$/, "") : url);

const HttpInterceptor = axios.create({
    baseURL: normalizeBaseUrl(import.meta.env.VITE_SERVER),
    withCredentials: true,
    timeout: API_TIMEOUT.DEFAULT,
    headers: {
        'Content-Type': 'application/json',
    }
});

// Request interceptor
HttpInterceptor.interceptors.request.use(
    (config) => {
        // Add any auth tokens or headers here if needed
        return config;
    },
    (error) => {
        console.error('Request error:', error);
        return Promise.reject(error);
    }
);

// Track shown errors to prevent duplicate toasts
const shownErrors = new Map(); // Map of errorKey -> lastShownTimestamp
const ERROR_COOLDOWN = 5000; // 5 seconds cooldown between same error messages

// Response interceptor for global error handling
HttpInterceptor.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        // Ignore aborted requests (don't show errors for cancelled requests)
        if (error.name === 'AbortError' || error.code === 'ERR_CANCELED' || error.message?.includes('aborted')) {
            return Promise.reject({
                ...error,
                message: 'Request cancelled',
                type: 'aborted'
            });
        }

        // Handle different types of errors
        if (error.code === 'ECONNABORTED' || error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
            // Timeout or network error
            // Create a unique key for this error type and endpoint
            const errorKey = `network-${error.config?.url || 'unknown'}`;
            const now = Date.now();
            const lastShown = shownErrors.get(errorKey);
            
            // Only show toast if not shown recently (prevent spam)
            if (error.config?.showErrorToast !== false) {
                if (!lastShown || (now - lastShown) > ERROR_COOLDOWN) {
                    toast.error(ERROR_MESSAGES.NETWORK);
                    shownErrors.set(errorKey, now);
                    // Clean up old entries after 1 minute
                    setTimeout(() => shownErrors.delete(errorKey), 60000);
                }
            }
            return Promise.reject({
                ...error,
                message: ERROR_MESSAGES.NETWORK,
                type: 'network'
            });
        }

        if (error.response) {
            // Server responded with error status
            const status = error.response.status;
            const data = error.response.data;
            const hasServerMessage = typeof data?.message === 'string' && data.message.trim().length > 0;
            let errorMessage = hasServerMessage ? data.message : ERROR_MESSAGES.UNKNOWN;

            switch (status) {
                case HTTP_STATUS.UNAUTHORIZED:
                    errorMessage = hasServerMessage ? data.message : ERROR_MESSAGES.UNAUTHORIZED;
                    // Only redirect to login for session-related endpoints
                    // Let Guard component handle session validation redirects
                    // This prevents redirect loops and allows SWR to retry session checks
                    // Don't redirect if on root path (/) - HomeRedirect handles that
                    const isSessionEndpoint = error.config?.url?.includes('/auth/session');
                    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
                    const shouldNotRedirect = currentPath.includes('/login') || currentPath === '/' || currentPath === '';
                    
                    // Suppress console error for 401 on session endpoint (expected when not logged in)
                    if (isSessionEndpoint) {
                        // Mark error as expected to prevent console logging
                        error.isExpected = true;
                        // Suppress axios default error logging for this expected error
                        error.suppressConsoleError = true;
                    }
                    
                    if (isSessionEndpoint && typeof window !== 'undefined' && !shouldNotRedirect) {
                        // Only redirect if it's a session check and not already on login page or root
                        // Use a longer delay to allow SWR to retry first
                        setTimeout(() => {
                            // Double-check we're still unauthorized and not on excluded paths before redirecting
                            const currentPathCheck = window.location.pathname;
                            if (!currentPathCheck.includes('/login') && currentPathCheck !== '/' && currentPathCheck !== '') {
                                window.location.href = '/login';
                            }
                        }, TIME_CONSTANTS.SECOND * 3); // 3 seconds delay to allow retries
                    }
                    break;
                case HTTP_STATUS.FORBIDDEN:
                    errorMessage = hasServerMessage ? data.message : ERROR_MESSAGES.FORBIDDEN;
                    break;
                case HTTP_STATUS.NOT_FOUND:
                    errorMessage = hasServerMessage ? data.message : ERROR_MESSAGES.NOT_FOUND;
                    break;
                case HTTP_STATUS.CONFLICT:
                    errorMessage = hasServerMessage ? data.message : ERROR_MESSAGES.CONFLICT;
                    break;
                case HTTP_STATUS.UNPROCESSABLE_ENTITY:
                    errorMessage = hasServerMessage ? data.message : ERROR_MESSAGES.VALIDATION;
                    break;
                case HTTP_STATUS.TOO_MANY_REQUESTS:
                    errorMessage = hasServerMessage ? data.message : ERROR_MESSAGES.TOO_MANY_REQUESTS;
                    break;
                case HTTP_STATUS.INTERNAL_SERVER_ERROR:
                    // Prefer server-provided message if present; otherwise fallback to friendly generic copy
                    errorMessage = hasServerMessage ? data.message : ERROR_MESSAGES.SERVER_ERROR;
                    break;
                case HTTP_STATUS.SERVICE_UNAVAILABLE:
                    errorMessage = hasServerMessage ? data.message : ERROR_MESSAGES.SERVICE_UNAVAILABLE;
                    break;
                default:
                    errorMessage = hasServerMessage ? data.message : `Error ${status}: ${ERROR_MESSAGES.UNKNOWN}`;
            }

            // Normalize technical/ambiguous 5xx messages into friendly copy for UI
            if (status >= HTTP_STATUS.INTERNAL_SERVER_ERROR) {
                const lower = (errorMessage || '').toLowerCase();
                if (lower.includes('internal server error') || lower.includes('server error')) {
                    errorMessage = ERROR_MESSAGES.SERVER_ERROR;
                }
            }

            // Normalize technical/ambiguous auth messages into friendly copy for UI
            if (status === HTTP_STATUS.UNAUTHORIZED) {
                const lower = (errorMessage || '').toLowerCase();
                if (
                    lower === 'unauthorized' ||
                    lower.includes('invalid session') ||
                    lower.includes('session not found') ||
                    lower.includes('invalid token')
                ) {
                    errorMessage = ERROR_MESSAGES.UNAUTHORIZED;
                }
            }
            if (status === HTTP_STATUS.FORBIDDEN) {
                const lower = (errorMessage || '').toLowerCase();
                if (lower === 'forbidden' || lower === 'unauthorized') {
                    errorMessage = ERROR_MESSAGES.FORBIDDEN;
                }
                if ((errorMessage || '').toLowerCase().startsWith('unauthorized:')) {
                    errorMessage = errorMessage.replace(/^unauthorized:\s*/i, '');
                }
                if ((errorMessage || '').toLowerCase().startsWith('unauthorized to ')) {
                    errorMessage = errorMessage.replace(/^unauthorized to /i, "You don't have permission to ");
                }
            }

            // Only show toast if not explicitly disabled
            if (error.config?.showErrorToast !== false && status >= HTTP_STATUS.INTERNAL_SERVER_ERROR) {
                // Only show toast for server errors (5xx), client errors are handled in components
                // Throttle server error toasts to prevent spam
                const errorKey = `server-${status}-${error.config?.url || 'unknown'}`;
                const now = Date.now();
                const lastShown = shownErrors.get(errorKey);
                
                if (!lastShown || (now - lastShown) > ERROR_COOLDOWN) {
                    toast.error(errorMessage);
                    shownErrors.set(errorKey, now);
                    setTimeout(() => shownErrors.delete(errorKey), 60000);
                }
            }

            // Ensure components reading `error.response.data.message` also see the normalized message
            const normalizedResponse = {
                ...error.response,
                data: {
                    ...(data || {}),
                    message: errorMessage
                }
            };

            return Promise.reject({
                ...error,
                response: normalizedResponse,
                message: errorMessage,
                type: 'server',
                status,
                data: data
            });
        }

        // Request was made but no response received (network errors, connection refused, etc.)
        if (error.request) {
            // Ignore aborted requests
            if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
                return Promise.reject({
                    ...error,
                    message: 'Request cancelled',
                    type: 'aborted'
                });
            }

            const errorKey = `no-response-${error.config?.url || 'unknown'}`;
            const now = Date.now();
            const lastShown = shownErrors.get(errorKey);
            
            if (error.config?.showErrorToast !== false) {
                if (!lastShown || (now - lastShown) > ERROR_COOLDOWN) {
                    toast.error(ERROR_MESSAGES.NO_RESPONSE);
                    shownErrors.set(errorKey, now);
                    setTimeout(() => shownErrors.delete(errorKey), 60000);
                }
            }
            return Promise.reject({
                ...error,
                message: ERROR_MESSAGES.NO_RESPONSE,
                type: 'network'
            });
        }

        // Something else happened
        const errorMessage = error.message || ERROR_MESSAGES.UNKNOWN;
        const errorKey = `unknown-${error.config?.url || 'unknown'}`;
        const now = Date.now();
        const lastShown = shownErrors.get(errorKey);
        
        if (error.config?.showErrorToast !== false) {
            if (!lastShown || (now - lastShown) > ERROR_COOLDOWN) {
                toast.error(errorMessage);
                shownErrors.set(errorKey, now);
                setTimeout(() => shownErrors.delete(errorKey), 60000);
            }
        }
        return Promise.reject({
            ...error,
            message: errorMessage,
            type: 'unknown'
        });
    }
);

export default HttpInterceptor;